import { useCallback } from 'react';
import { useAuth } from '../context/AuthContext.jsx';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

/**
 * Returns a fetch wrapper that automatically attaches the JWT auth header.
 * Usage:
 *   const api = useApi();
 *   const data = await api.get('/employees');
 *   const result = await api.post('/employees', { name: 'Alice', role: 'kitchen' });
 */
export function useApi() {
  const { getToken, logout } = useAuth();

  const request = useCallback(async (method, path, body = undefined, options = {}) => {
    const token = getToken();
    const headers = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    };

    const config = {
      method,
      headers,
    };

    if (body !== undefined) {
      config.body = JSON.stringify(body);
    }

    const url = path.startsWith('http') ? path : `${API_BASE}${path}`;

    const res = await fetch(url, config);

    // Auto-logout on 401
    if (res.status === 401) {
      logout();
      throw new ApiError('Session expired. Please log in again.', 401);
    }

    let data;
    const contentType = res.headers.get('Content-Type') || '';
    if (contentType.includes('application/json')) {
      data = await res.json();
    } else {
      data = { message: await res.text() };
    }

    if (!res.ok) {
      throw new ApiError(data.error || data.message || 'Request failed', res.status, data);
    }

    return data;
  }, [getToken, logout]);

  return {
    get: (path, options) => request('GET', path, undefined, options),
    post: (path, body, options) => request('POST', path, body, options),
    put: (path, body, options) => request('PUT', path, body, options),
    delete: (path, options) => request('DELETE', path, undefined, options),
    patch: (path, body, options) => request('PATCH', path, body, options),
  };
}

export class ApiError extends Error {
  constructor(message, status, data = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}
